import {Injectable} from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import {JsonApiResponse} from '../../shared/models/JsonApiResponse';
import {environment} from '../../../environments/environment';
import {catchError, map} from 'rxjs/operators';
import {HttpErrorHandler} from '../../http.error.handler';
import {
  DataSourceModel,
  LayerModel,
  RouteConfiguration,
  RouteConfigurationAuth,
  RouteConfigurationChangeLog,
  RouteConfigurationWithSchema,
  TaskStatus,
} from '../../shared/models/route';
import {FeatureCollection} from 'geojson';
import {GeobufResponseHandler} from '../../geobuf.response.handler';
import {firstValueFrom} from 'rxjs';
import pako from 'pako';

@Injectable({
  providedIn: 'root'
})
export class RoutesService {

  constructor(private readonly http: HttpClient) {
  }

  getFeatureServiceDescription(url: string, auth?: RouteConfigurationAuth) {
    const encodedUrl = encodeURIComponent(url);
    return firstValueFrom(
      this.http
        .post<JsonApiResponse<DataSourceModel>>(
          `${environment.services.service}v1/datasource/describe?url=${encodedUrl}`,
          auth,
          {headers: {'Content-Type': 'application/json'}}, // add content-type header explicitly on empty body
        )
        .pipe(
          catchError(HttpErrorHandler.handleError)
        )
    );
  }

  getFeatureServiceLayerDescription(url: string, layerId: number, auth?: RouteConfigurationAuth) {
    const encodedUrl = encodeURIComponent(url);
    return firstValueFrom(
      this.http
        .post<JsonApiResponse<LayerModel>>(
          `${environment.services.service}v1/datasource/${layerId}/describe?url=${encodedUrl}`,
          auth,
          {headers: {'Content-Type': 'application/json'}},
        )
        .pipe(
          catchError(HttpErrorHandler.handleError)
        )
    );
  }

  getFeatureTaskStatus(
    taskUuid: string,
  ) {
    return firstValueFrom(
      this.http.get<JsonApiResponse<TaskStatus>>(
        `${environment.services.service}v1/datasource/task-status/${taskUuid}`
      )
        .pipe(
          catchError(HttpErrorHandler.handleError)
        )
    );
  }

  cleanFeatureTaskStatus(
    taskUuid: string,
  ) {
    return firstValueFrom(
      this.http.delete(
        `${environment.services.service}v1/datasource/task-status/${taskUuid}`
      )
        .pipe(
          catchError(HttpErrorHandler.handleError)
        )
    );
  }

  // start feature import and get task UUID
  readFeatureCollectionFromFeatureProxyAsync(
    url: string,
    layerId: number,
    orderedFields: string[],
    auth: RouteConfigurationAuth,
  ) {
    const encodedUrl = encodeURIComponent(url);
    return firstValueFrom(
      this.http
        .post<JsonApiResponse<string>>(
          `${environment.services.service}v1/datasource/${layerId}/features?url=${encodedUrl}&orderedFields=${orderedFields.join(':::')}`,
          auth,
          {headers: {'Content-Type': 'application/json'}},
        )
        .pipe(
          catchError(HttpErrorHandler.handleError)
        )
    );
  }

  // upload feature collection and get task UUID
  uploadFeatureCollection(featureCollection: FeatureCollection) {
    const json = JSON.stringify(featureCollection);
    const compressed = pako.gzip(json);
    const blob = new Blob([compressed], { type: 'application/json' });

    return firstValueFrom(
      this.http.post<JsonApiResponse<string>>(
        `${environment.services.service}v1/datasource/upload`,
        blob,
        {
          headers: {
            'Content-Encoding': 'gzip',
          },
        }
      )
        .pipe(
          catchError(HttpErrorHandler.handleError)
        )
    );
  }

  getRouteConfigurationFromFeatureProxy(
    taskUuid: string,
    routeConfigurationId: number,
    url: string,
    layerId: number,
    orderedFields: string[],
    subRouteField: string | undefined,
  ) {
    let params: HttpParams = new HttpParams()
      .set('url', encodeURIComponent(url))
      .set('orderedFields', orderedFields.join(':::'));
    if (routeConfigurationId !== null && routeConfigurationId !== undefined) {
      params = params.set('routeConfigurationId', routeConfigurationId);
    }
    if (subRouteField) {
      params = params.set('subRouteField', subRouteField);
    }
    return firstValueFrom(
      this.http.get<JsonApiResponse<RouteConfiguration>>(
        `${environment.services.service}v1/datasource/${layerId}/features/configuration/${taskUuid}`, {params}
      )
        .pipe(
          catchError(HttpErrorHandler.handleError)
        )
    );
  }

  getRoutesGeoJsonFromExternalService(
    taskUuid: string,
    layerId: number,
    orderedFields: string[],
    subRouteField: string | undefined,
    routeConfiguration: RouteConfiguration,
  ) {
    let params: HttpParams = new HttpParams()
      .set('orderedFields', orderedFields.join(':::'));
    if (subRouteField) {
      params = params.set('subRouteField', subRouteField);
    }
    return firstValueFrom(
      this.http.post<JsonApiResponse<FeatureCollection>>(
        `${environment.services.service}v1/datasource/${layerId}/features/geojson/${taskUuid}`,
        routeConfiguration,
        {params}
      )
        .pipe(
          catchError(HttpErrorHandler.handleError)
        )
    );
  }

  getRouteConfigurations() {
    return firstValueFrom(this.http.get<JsonApiResponse<RouteConfigurationWithSchema[]>>(
        `${environment.services.service}v1/route-management/configuration/list`, {}
      )
        .pipe(
          catchError(HttpErrorHandler.handleError)
        )
    );
  }

  getRouteConfiguration(configId: number) {
    return firstValueFrom(
      this.http.get<JsonApiResponse<RouteConfigurationWithSchema>>(
        `${environment.services.service}v1/route-management/configuration/${configId}`, {}
      )
        .pipe(
          catchError(HttpErrorHandler.handleError)
        )
    );
  }

  updateRouteConfiguration(routeConfiguration: RouteConfigurationWithSchema) {
    return firstValueFrom(this.http.post<JsonApiResponse<RouteConfigurationWithSchema>>(
      `${environment.services.service}v1/route-management/configuration`,
      routeConfiguration
    )
      .pipe(
        catchError(HttpErrorHandler.handleError)
      ));
  }

  getRouteGeoJson() {
    return firstValueFrom(
      this.http.get(
        `${environment.services.service}v1/route-management/allConfigurations/geometry`,
        {
          observe: 'response',
          responseType: 'arraybuffer',
          headers: {Accept: 'application/octet-stream'}
        }
      )
        .pipe(
          map(response => GeobufResponseHandler.handleResponse(response)),
          catchError(HttpErrorHandler.handleError) // then handle the error
        )
    );
  }

  getRouteGeoJsonById(id: number) {
    return firstValueFrom(
      this.http.get<FeatureCollection>(
        `${environment.services.service}v1/route-management/configuration/${id}/geometry`,
      )
        .pipe(
          catchError(HttpErrorHandler.handleError) // then handle the error
        )
    );
  }

  updateRouteGeoJson(taskUuid: string, configId: number, routeConfiguration: RouteConfigurationWithSchema) {
    return firstValueFrom(this.http.post(
      `${environment.services.service}v1/route-management/configuration/${configId}/geometry/${taskUuid}`,
      routeConfiguration
    )
      .pipe(
        catchError(HttpErrorHandler.handleError)
      ));
  }

  copyRouteConfiguration(configId: number, configurationName: string) {
    return firstValueFrom(
      this.http
        .post(`${environment.services.service}v1/route-management/configuration/${configId}/copy`, { name: configurationName })
        .pipe(
          catchError(HttpErrorHandler.handleError) // then handle the error
        ));
  }

  deleteRouteConfiguration(configId: number) {
    return firstValueFrom(this.http.delete(`${environment.services.service}v1/route-management/configuration/${configId}`)
      .pipe(
        catchError(HttpErrorHandler.handleError) // then handle the error
      ));
  }

  getRouteLength(configId: number, routeId: number) {
    return firstValueFrom(
      this.http.get<JsonApiResponse<number>>(
        `${environment.services.service}v1/route-management/configuration/${configId}/route/${routeId}/length`, {}
      )
        .pipe(
          catchError(HttpErrorHandler.handleError)
        )
    );
  }

  getPublicCustomLayersRouteGeoJson(customerId: number) {
    return firstValueFrom(
      this.http.get(
        `${environment.services.service}public/v1/routeSource?secret=${customerId}`,
        {
          observe: 'response',
          responseType: 'arraybuffer',
          headers: {Accept: 'application/octet-stream'}
        }
      )
        .pipe(
          map(response => GeobufResponseHandler.handleResponse(response)),
          catchError(HttpErrorHandler.handleError) // then handle the error
        )
    );
  }

  getRouteConfigurationChangeLog(configId: number) {
    return firstValueFrom(
      this.http.get<JsonApiResponse<RouteConfigurationChangeLog[]>>(
        `${environment.services.service}v1/route-management/configuration/${configId}/changelog`, {}
      )
        .pipe(
          map(response => response.data),
          catchError(HttpErrorHandler.handleError)
        )
    );
  }
}
